import { load } from "@loaders.gl/core";
import { PLYLoader as PLY } from "@loaders.gl/ply";
import {
  Box3,
  InterleavedBufferAttribute,
  LoadingManager,
  Points,
  Vector3,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import {
  IPointCloudLoader,
  IPointCloudLoadParams,
  IPointCloudTileGeometry,
} from "./interface";
import { getZipResponse } from "./utils";
import { DndProvider } from "react-dnd";

export class PLYLoader implements IPointCloudLoader {
  async load(
    params: IPointCloudLoadParams,
    abortController: AbortController
  ): Promise<IPointCloudTileGeometry> {
    const response = await getZipResponse(
      params.url as string,
      abortController
    );
    if (response instanceof Blob) {
      // PLY
      const data = await load(response, PLY, {
        fetch: { credentials: "include", signal: abortController.signal },
      });
      const bbox = new Box3().setFromArray([
        ...data.header.boundingBox[0],
        ...data.header.boundingBox[1],
      ]);
      const center = new Vector3();
      bbox.getCenter(center);
      const offset = center.negate();
      bbox.translate(offset);
      const vertices = new Float32Array(data.attributes.POSITION.value.length); // xyz
      for (let i = 0; i < data.attributes.POSITION.value.length; i += 3) {
        vertices[i] = data.attributes.POSITION.value[i] + offset.x;
        vertices[i + 1] = data.attributes.POSITION.value[i + 1] + offset.y;
        vertices[i + 2] = data.attributes.POSITION.value[i + 2] + offset.z;
      }

      const color = new Float32Array(data.attributes.COLOR_0.value.length); // データからR, G, Bを抽出
      for (let i = 0; i < data.attributes.COLOR_0.value.length; i++) {
        color[i] = data.attributes.COLOR_0.value[i] / 255; // 0-255で来るRGBを0-1に変換
      }
      return {
        vertices,
        color,
        offset,
        bbox,
      };
    } else {
      // GLTF
      const manager = new LoadingManager();
      manager.setURLModifier((url) => {
        url = window.URL.createObjectURL(response[url]);
        return url;
      });
      const loader = new GLTFLoader(manager);
      let gltfFile: string;
      Object.keys(response).forEach((key) => {
        const ext = key.split(".").reverse()[0];
        // console.log(ext);
        if (ext === "gltf") {
          gltfFile = key;
        }
      });

      const load = new Promise<IPointCloudTileGeometry>((resolve, reject) => {
        loader.load(gltfFile, async (gltf) => {
          // console.log(gltf);
          let pointcloud: Points;
          for (const [key, value] of gltf.parser.associations) {
            // console.log(key)
            if (key instanceof Points) {
              pointcloud = key as Points;
            }
          }
          // console.log(pointcloud)
          const bbox = pointcloud.geometry?.boundingBox.clone();
          const center = new Vector3();
          bbox?.getCenter(center);
          const offset = center.negate();
          bbox?.translate(offset);
          const vertices = new Float32Array(
            pointcloud.geometry.attributes.position.array.length / 2
          ); // xyz
          const posOffset =
            (
              pointcloud.geometry?.attributes
                ?.position as InterleavedBufferAttribute
            )?.offset || 0;
          const posStride =
            (
              pointcloud.geometry?.attributes
                ?.position as InterleavedBufferAttribute
            )?.data?.stride || 3;

          for (
            let i = 0;
            i <
            pointcloud.geometry.attributes.position.array.length / posStride;
            ++i
          ) {
            vertices[3 * i] =
              pointcloud.geometry.attributes.position.array[
                posStride * i + posOffset
              ] + offset.x;
            vertices[3 * i + 1] =
              pointcloud.geometry.attributes.position.array[
                posStride * i + 1 + posOffset
              ] + offset.y;
            vertices[3 * i + 2] =
              pointcloud.geometry.attributes.position.array[
                posStride * i + 2 + posOffset
              ] + offset.z;
          }

          const color = new Float32Array(
            pointcloud.geometry.attributes.color.array.length
          ); // rgb

          const divideFactor = pointcloud.geometry.attributes.color.normalized
            ? 255
            : 1;

          for (let i = 0; i < pointcloud.geometry.attributes.color.count; ++i) {
            color[3 * i] =
              pointcloud.geometry.attributes.color.getX(i) / divideFactor;
            color[3 * i + 1] =
              pointcloud.geometry.attributes.color.getY(i) / divideFactor;
            color[3 * i + 2] =
              pointcloud.geometry.attributes.color.getZ(i) / divideFactor;
          }

          resolve({
            vertices,
            color,
            offset,
            bbox,
          });
        });
      });
      return await load;
    }
  }
}
